/* eslint-disable eslint-comments/no-restricted-disable */
// entrypoint.tsx

import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { JiraFieldConfigurationAri } from '@atlassian/ari/jira';
import { fg } from '@atlassian/jira-feature-gating';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import parameters from './__generated__/FieldsPageViewQuery$parameters';
import type {
	FieldsPageViewQuery,
	JiraFieldScopeType,
} from './__generated__/FieldsPageViewQuery.graphql';

// Backwards compatibility with old URL params
const CustomFieldStatus = {
	TRASHED: 'trashed',
	ACTIVE: 'active',
} as const;

type InitialTab = 'active' | 'deleted';

// These params will sync state to the URL
const URL_PARAMS = {
	SIDEBAR_ID: 'sidebarId',
	SIDEBAR_VIEW: 'sidebarView',
	FIELD_ID: 'fieldId',
	SEARCH_QUERY: 'searchQuery',
	FIELD_TYPE_GROUPS: 'fieldTypeGroups',
	FIELD_SCOPE: 'fieldScope',
	SORTING_COLUMN_ID: 'sortingColumnId',
	SORTING_DIRECTION: 'sortingDirection',
	// This is the intitialTab named like this to be backwards compatible with legacy experience
	customFieldStatus: 'customFieldStatus',
} as const;

type GlobalSettingsIssueFieldsManagementRouteEntryPointComponent = EntryPointComponent<
	{
		globalFields: FieldsPageViewQuery;
	},
	{},
	{},
	{
		initialTab: 'active' | 'deleted';
		isAdmin: boolean;
	}
>;

export const globalSettingsIssueFieldsManagementRouteEntryPoint: EntryPoint<
	GlobalSettingsIssueFieldsManagementRouteEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<GlobalSettingsIssueFieldsManagementRouteEntryPointComponent>(
		/* webpackChunkName: "async-jira-admin-pages-fields-page" */ './GlobalSettingsAdminPagesFields.tsx',
		{
			entryFsSsrLiftImportToModule: true,
		},
	),
	getPreloadProps: ({
		tenantContext: { cloudId, activationId, isAdmin },
		context: { query },
	}: EntryPointRouteParams) => {
		const sidebarId = query[URL_PARAMS.SIDEBAR_ID]?.toUpperCase();
		const sidebarView = query[URL_PARAMS.SIDEBAR_VIEW]?.toLowerCase();
		const fieldId = query[URL_PARAMS.FIELD_ID];
		const initialTab: InitialTab =
			query[URL_PARAMS.customFieldStatus]?.toLowerCase() === CustomFieldStatus.TRASHED
				? 'deleted'
				: 'active';
		const searchQuery = (() => {
			const rawValue = query[URL_PARAMS.SEARCH_QUERY];
			return rawValue ? decodeURIComponent(rawValue) : undefined;
		})();
		const fieldTypeGroups = (() => {
			const rawValue = query[URL_PARAMS.FIELD_TYPE_GROUPS];
			if (!rawValue) return undefined;
			const groups = decodeURIComponent(rawValue).split(',').filter(Boolean);
			return groups.length > 0 ? groups : undefined;
		})();
		const sortingColumnId = (() => {
			const rawValue = query[URL_PARAMS.SORTING_COLUMN_ID];
			return rawValue ? decodeURIComponent(rawValue) : undefined;
		})();
		const sortingDirection = query[URL_PARAMS.SORTING_DIRECTION];
		const preloadFieldNodeId = fieldId
			? (() => {
					try {
						return JiraFieldConfigurationAri.create({
							fieldConfigurationId: fieldId,
							activationId,
							siteId: cloudId,
						}).toString();
					} catch (error) {
						return '';
					}
				})()
			: '';
		const fieldScope: JiraFieldScopeType = 'ALL';
		/**
		 * We have to distinguish which tab the url params are for
		 */
		const initialRawFilters =
			initialTab === 'deleted'
				? {
						deletedSearchQuery: searchQuery ?? '',
						deletedFieldTypeGroups: fieldTypeGroups,
						deletedFieldScope: fieldScope,
						deletedSortingColumnId: sortingColumnId,
						deletedSortingDirection: sortingDirection,
					}
				: {
						activeSearchQuery: searchQuery ?? '',
						activeFieldTypeGroups: fieldTypeGroups,
						activeFieldScope: fieldScope,
						activeSortingColumnId: sortingColumnId,
						activeSortingDirection: sortingDirection,
					};
		return {
			queries: {
				globalFields: {
					options: {
						fetchPolicy: 'network-only' as const,
					},
					parameters,
					variables: {
						cloudId,
						isAdmin,
						preloadFieldNodeId,
						shouldPreloadField: !!preloadFieldNodeId,
						includeFieldId: fg('centralized-fields-allow-editing-global-cf-options'),
						includeInstalledByAppName: fg('cfm_add_app_name'),
						fieldScope,
						activeSearchQuery: initialTab === 'active' ? (searchQuery ?? '') : '',
						deletedSearchQuery: initialTab === 'deleted' ? (searchQuery ?? '') : '',
						...initialRawFilters,
						includeFieldSchemeOperations: true,
						// Never included for deleted fields
						includeFieldSchemeOperationsForDeleted: false,
						includeOptionsCount: fg('slt1-987_add_options_count_fields_table'),
						includeProjectInfo: true,
						filterKeys: ['field.options.per.field.limit'],
					},
				},
			},
			extraProps: {
				initialTab,
				sidebarId,
				sidebarView,
				isAdmin,
				fieldNodeId: preloadFieldNodeId,
				searchQuery,
				fieldTypeGroups,
				sortingColumnId,
				sortingDirection,
			},
		};
	},
});
